window._debugMode = true;

import React, { useEffect, useRef, useMemo, useCallback } from "react";

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";

import { classNames, generateRandomString } from "utility/Utils";
import { StateDebugger, useDebugState } from "utility/StateDebugger";

interface CustomDropDownProps {
    buttonBorderColor?: string;
    buttonFont?: string;
    buttonLabelMaxLength?: number;
    buttonTextColor?: string;
    defaultValue: any;
    displayDisabledWhenLocked?: boolean;
    locked?: boolean;
    optionMaxHeight?: string;
    optionPillBackgroundColors?: string[];
    optionPillTextColors?: string[];
    options?: any[];
    optionsAlignRight?: boolean;
    optionsJustifyBottom?: boolean;
    autoHeight?: boolean; // New prop to enable auto-height behavior
    // Callbacks
    onSelectionCallback: (selectedItem: unknown) => void;
    // debugging
    debugState?: boolean;
}

const CustomDropDown = ({
    buttonBorderColor = "border-gray-300",
    buttonFont = "font-semibold",
    buttonLabelMaxLength = 0,
    buttonTextColor = "text-gray-700",
    defaultValue = null,
    displayDisabledWhenLocked = true,
    locked = false,
    optionMaxHeight = null,
    optionPillBackgroundColors = null,
    optionPillTextColors = null,
    options = [],
    optionsAlignRight = true,
    optionsJustifyBottom = true,
    autoHeight = false,
    // Callbacks
    onSelectionCallback = null,
    // debugging
    debugState = false
}: CustomDropDownProps) => {

    const dropdownRef = useRef<HTMLDivElement>(null);
    const listboxOptionsRef = useRef<HTMLDivElement>(null);

    const debugKeyRef = useRef(`CustomDropDown-${generateRandomString(6)}`);
    const debugKey = debugKeyRef.current;

    const [buttonClassName, setButtonClassName] = useDebugState(null, "buttonClassName", debugKey, debugState);
    const [buttonLabel, setButtonLabel] = useDebugState(null, "buttonLabel", debugKey, debugState);
    const [currentSelection, setCurrentSelection] = useDebugState(null, "currentSelection", debugKey, debugState);
    const [dynamicMaxHeight, setDynamicMaxHeight] = useDebugState(null, "dynamicMaxHeight", debugKey, debugState);
    const [focusClassName, setFocusClassName] = useDebugState(null, "focusClassName", debugKey, debugState);
    const [optionClassName, setOptionClassName] = useDebugState(null, "optionClassName", debugKey, debugState);
    const [selectOptions, setSelectOptions] = useDebugState(null, "selectOptions", debugKey, debugState);

    // ---=== State Debugging ===--- //

    const myRenderCount = useRef(0);
    myRenderCount.current += 1;
    if (debugState) {
        let initialSelection = options.find(option => option.value === defaultValue) || options[0];
        console.log(`[${debugKey}][${currentSelection ? currentSelection.value : initialSelection.value}][render(${myRenderCount.current})]`);
    }

    // ---=== Utilities ===--- //

    // Prepare options with React keys for use in Listbox dropdowns.
    // IMPORTANT!: Do not add keys dynamically inside the Listbox as this
    // will create new React elements with updated state on every render!
    const preparedOptions = useMemo(() => {
        if (debugState) {
            let initialSelection = options.find(option => option.value === defaultValue) || options[0];
            console.info(`[${debugKey}][${currentSelection ? currentSelection.value : initialSelection.value}] Generating memoized selection options with new keys for Listbox!`)
        }
        return options.map(option => ({
            ...option,
            key: option.key ?? option.value
        }));
    }, [options]);

    const createButtonLabelState = (initialSelection) => {
        setButtonLabel(
            buttonLabelMaxLength === 0 || initialSelection.label.length <= buttonLabelMaxLength
                ? initialSelection.label
                : initialSelection.label.slice(0, buttonLabelMaxLength) + "..."
        );
    };

    const createButtonClassNameState = (initialOptions) => {
        setButtonClassName(
            classNames(
                `relative w-full cursor-default border justify-start items-center gap-2 inline-flex py-2.5 pl-3 pr-10 text-left text-sm shadow ring-0 ring-inset focus:outline-none focus:ring-2 basis-0 self-stretch rounded-lg`,
                initialOptions?.length === 0 || (locked && displayDisabledWhenLocked) ? "text-gray-400 bg-gray-50" : `${buttonTextColor} bg-white`,
                buttonFont,
                buttonBorderColor,
                initialOptions?.length === 0 ? "ring-gray-300/5" : "ring-gray-300",
                initialOptions?.length === 0 || (locked && displayDisabledWhenLocked) ? "focus:ring-gray-300" : "focus:ring-brand-500",
                initialOptions?.length === 0 || (locked) ? "" : "hover:bg-gray-100",
                initialOptions?.length === 0 || (locked) ? "" : "cursor-pointer"
            )
        );
    };

    const calculateDynamicHeight = useCallback(() => {
        if (!autoHeight || !dropdownRef.current || !selectOptions) {
            return null;
        }

        const buttonRect = dropdownRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const availableSpace = optionsJustifyBottom
            ? viewportHeight - buttonRect.bottom - 20 // 20px padding from bottom
            : buttonRect.top - 20; // 20px padding from top

        // More accurate height estimation - each option is roughly 44px (py-2 = 16px + content ~28px)
        const estimatedOptionHeight = 44;
        const estimatedTotalHeight = selectOptions.length * estimatedOptionHeight + 16; // 16px for container padding

        if (estimatedTotalHeight <= availableSpace) {
            // All options fit, return null to use max-h-none
            return null;
        }
        else {
            // Not enough space, calculate scrollable height
            const maxScrollableHeight = Math.max(176, availableSpace - 20); // Minimum ~4 options, with buffer
            return Math.floor(maxScrollableHeight);
        }
    }, [autoHeight, selectOptions, optionsJustifyBottom]);

    const createOptionClassNameState = () => {
        const baseClasses = "absolute z-10 overflow-y-auto overflow-x-hidden rounded-md bg-white text-base shadow-lg ring-1 ring-black/10 focus:outline-none sm:text-sm cursor-pointer";
        const positionClasses = `w-max min-w-full ${optionsAlignRight ? "right-0" : "left-0"} ${optionsJustifyBottom ? "mt-1" : "bottom-0 mb-10"}`;

        let heightClass;
        if (autoHeight) {
            // We'll handle height with inline styles when autoHeight is enabled
            heightClass = "";
        }
        else if (optionMaxHeight !== null) {
            heightClass = optionMaxHeight;
        }
        else {
            heightClass = "max-h-56";
        }

        setOptionClassName(classNames(baseClasses, positionClasses, heightClass));
    };

    const createFocusClassNameState = () => {
        setFocusClassName("relative cursor-default select-none py-2 px-3 w-full whitespace-nowrap cursor-pointer");
    };

    // ---=== Render ===--- //

    useEffect(() => {
        if (!options || options.length === 0) {
            return;
        }

        const initialSelection = options.find(option => option.value === defaultValue) || options[0];

        // Always update currentSelection when defaultValue changes
        setCurrentSelection(initialSelection);

        if (debugState) {
            console.info(`[${debugKey}][${initialSelection.value}] Setting memoized selection options for Listbox!`);
        }
        let initialOptions = preparedOptions;
        setSelectOptions(initialOptions);

        createButtonLabelState(initialSelection);
        createButtonClassNameState(initialOptions);
        createOptionClassNameState();
        createFocusClassNameState();

    }, [defaultValue, options, locked, preparedOptions]);

    useEffect(() => {
        if (currentSelection) {
            createButtonLabelState(currentSelection);
        }
    }, [currentSelection, buttonLabelMaxLength]);

    const renderOption = (option, selected: boolean) => {
        return (
            <span className="w-full">
                <div className="inline-flex items-center whitespace-nowrap gap-x-2 w-full">
                    {
                        (option.img) && <img src={option.img} className="h-5 w-5 flex-shrink-0 rounded-full" alt="icon"/>
                    }
                    {
                        (option.label) && (
                            <span
                                className={
                                    classNames(
                                        "flex-1",
                                        selected ? "font-semibold" : "font-normal"
                                    )
                                }
                            >
                                { option.label }
                            </span>
                        )
                    }
                    {
                        (option.pill) && (
                            <span className="ml-auto">
                                <div className={`px-2 py-0.5 rounded-full gap-x-1 justify-start items-center inline-flex ${optionPillBackgroundColors[option.pill.color]}`}>
                                    {
                                        (option.pill.img) && <img className="w-3 h-3 relative" src={option.pill.img} alt="badge"/>
                                    }
                                    <div className={`text-center text-xs font-medium leading-[18px] ${optionPillTextColors[option.pill.color]}`}>
                                        { option.pill.displayPercent ? `${Math.round(option.pill.value * 100)}%` : option.pill.value }
                                    </div>
                                </div>
                            </span>
                        )
                    }
                </div>
            </span>
        )
    };

    const renderListbox = () => {
        return (
            <StateDebugger debugKey={debugKey}>
                <Listbox
                    disabled={locked}
                    value={currentSelection}
                    onChange={
                        (selection) => {
                            if (debugState) {
                                console.log(`[${debugKey}] Listbox.onChange -> Start: selection[${selection.value}] previous[${currentSelection.value}]`);
                            }
                            if (selection.value !== currentSelection.value) {
                                setCurrentSelection(selection);
                                onSelectionCallback?.(selection.value);
                            }
                            else {
                                if (debugState) {
                                    console.log(`[${debugKey}] Listbox.onChange: selection has not changed, skipping onSelectionCallback callback.`)
                                }
                            }
                            if (debugState) {
                                console.log(`[${debugKey}] Listbox.onChange <- End: selection[${selection.value}] previous[${currentSelection.value}]`);
                            }
                        }
                    }
                >
                    {
                        ({ open }) => {
                        // Calculate dynamic height when dropdown is open
                            const dynamicHeight = open && autoHeight ? calculateDynamicHeight() : null;
                            const dynamicStyle = dynamicHeight ? { maxHeight: `${dynamicHeight}px` } : {};

                            return (
                                <span className="w-full">
                                    <div className="relative w-full" ref={dropdownRef}>
                                        <ListboxButton className={buttonClassName}>
                                            <span className="flex font-semibold	items-center">
                                                {
                                                    (currentSelection.label) && <span className="block truncate">{buttonLabel}</span>
                                                }
                                            </span>
                                            {
                                                (!locked) && (
                                                    <span className="pointer-events-none absolute inset-y-0 right-0 ml-2 flex items-center pr-2">
                                                        <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                                    </span>
                                                )
                                            }
                                        </ListboxButton>
                                        {
                                            (selectOptions?.length === 0)
                                                ? (
                                                    <span className="flex font-semibold	items-center truncate">
                                                        No Options Found
                                                    </span>
                                                )
                                                : (
                                                    <ListboxOptions
                                                        className={optionClassName}
                                                        style={dynamicStyle}
                                                        ref={listboxOptionsRef}
                                                    >
                                                        {
                                                            selectOptions.map(
                                                                (option) => (
                                                                    <ListboxOption
                                                                        disabled={option.disabled ?? false}
                                                                        key={option.key}
                                                                        value={option}
                                                                        className={
                                                                            ({ focus }) =>
                                                                                `${focusClassName} ${option.disabled ? "text-gray-400" : (focus ? "text-white bg-brand-500" : "text-brand-700")}`
                                                                        }
                                                                    >
                                                                        { ({ selected }) => renderOption(option, selected) }
                                                                    </ListboxOption>
                                                                )
                                                            )
                                                        }
                                                    </ListboxOptions>
                                                )
                                        }
                                    </div>
                                </span>
                            );
                        }
                    }
                </Listbox>
            </StateDebugger>
        );
    };

    return (selectOptions?.length && currentSelection) ? renderListbox() : null;
};

const areEqual = (prev, next) => {
    return (
        prev.autoHeight === next.autoHeight &&
        prev.buttonBorderColor === next.buttonBorderColor &&
        prev.buttonFont === next.buttonFont &&
        prev.buttonLabelMaxLength === next.buttonLabelMaxLength &&
        prev.buttonTextColor === next.buttonTextColor &&
        prev.defaultValue === next.defaultValue &&
        prev.locked === next.locked &&
        prev.displayDisabledWhenLocked === next.displayDisabledWhenLocked &&
        prev.isLocked === next.isLocked &&
        prev.optionMaxHeight === next.optionMaxHeight &&
        prev.optionPillBackgroundColors === next.optionPillBackgroundColors &&
        prev.optionPillTextColors === next.optionPillTextColors &&
        JSON.stringify(prev.options) === JSON.stringify(next.options) && // deep compare
        prev.optionsAlignRight === next.optionsAlignRight &&
        prev.optionsJustifyBottom === next.optionsJustifyBottom &&
        // Callbacks
        prev.onSelectionCallback === next.onSelectionCallback
    );
};

export default React.memo(CustomDropDown, areEqual);

// <Transition
//     show={open && !locked}
//     as={Fragment}
//     leave="transition ease-in duration-100"
//     leaveFrom="ring-black/10"
//     leaveTo="ring-black/0"
// >
//     <ListboxOptions className={optionClassName}>
//         {
//             selectOptions.map(
//                 (option) => (
//                     <ListboxOption
//                         key={option.key}
//                         value={option}
//                         className={
//                             ({ focus }) =>
//                                 `${focusClassName} ${focus ? "text-white bg-brand-500" : "text-brand-700"}`
//                         }
//                     >
//                         { ({ selected }) => renderOption(option, selected) }
//                     </ListboxOption>
//                 )
//             )
//         }
//     </ListboxOptions>
// </Transition>
